import { Component } from '@angular/core';
import {
  ItemAttributeCreatePayload,
  ItemAttributesStoreService
} from '../item-attributes-store.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { EditAttributeFormComponent } from '../edit-attribute-form/edit-attribute-form.component';
import { NgIf, NgFor } from '@angular/common';
import { TestIdDirective } from '../../directives/test-id.directive';

@Component({
    selector: 'app-add-attribute',
    templateUrl: './add-attribute.component.html',
    standalone: true,
    imports: [TestIdDirective, NgIf, EditAttributeFormComponent, MatButton, NgFor]
})
export class AddAttributeComponent {
  showCustomForm = false;
  templateOptions = Array.from(templates.keys()).map((templateKey) => ({
    key: templateKey,
    label: templates.get(templateKey).name
  }));

  form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    starting_value: new FormControl<string>('', Validators.required),
    data_type: new FormControl<'string' | 'number' | 'boolean' | 'date'>(
      'string',
      Validators.required
    ),
    type: new FormControl('custom'),
    prompts: new FormGroup({
      on_hire: promptFormGroupFactory(),
      off_hire: promptFormGroupFactory(),
      workshop_job_complete: promptFormGroupFactory(),
      collection: promptFormGroupFactory(),
      delivery: promptFormGroupFactory()
    }),
    append_to_hire_item_notes: new FormControl<boolean>(false),
    display_on_summary: new FormControl<boolean>(false),
    display_on_public_page: new FormControl<boolean>(false)
  });

  valueTypes = [
    { value: 'string', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'date', label: 'Date' }
  ];

  constructor(private store: ItemAttributesStoreService) {}

  createFromTemplate(templateName: templateKey) {
    const template = templates.get(templateName);
    this.store.addAttribute(template);
  }

  create() {
    this.store.addAttribute(this.form.value as ItemAttributeCreatePayload);
  }

  cancel() {
    this.store.setView('list');
  }
}

export function promptFormGroupFactory() {
  return new FormGroup({
    enabled: new FormControl(false),
    type: new FormControl<'inline' | 'popup' | undefined>(undefined)
  });
}

/*
const INSPECTION_INTERVAL_WEEKS = 'inspectionIntervalWeeks';
const LAST_INSPECTION_DATE = 'lastInspectionDate';

const MOT_EXPIRY_DATE = 'motExpiryDate';
const TAX_EXPIRY_DATE = 'taxExpiryDate';
const INSURANCE_EXPIRY_DATE = 'insuranceExpiryDate';
*/
export type templateKey =
  | 'engineHours'
  | 'odometerMiles'
  | 'lastServiceDate'
  | 'nextServiceDate'
  | 'serviceIntervalMiles'
  | 'serviceIntervalHours'
  | 'serviceIntervalMonths'
  | 'lastServiceHours'
  | 'lastServiceMiles'
  | 'inspectionIntervalWeeks'
  | 'lastInspectionDate'
  | 'motExpiryDate'
  | 'taxExpiryDate'
  | 'insuranceExpiryDate';

const templates: Map<templateKey, ItemAttributeCreatePayload> = new Map([
  [
    'engineHours',
    {
      name: 'Engine Hours',
      starting_value: '0',
      data_type: 'number',
      type: 'engineHours',
      prompts: {
        on_hire: {
          enabled: true,
          type: 'inline'
        },
        off_hire: {
          enabled: true,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'inline'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: true
    }
  ],
  [
    'odometerMiles',
    {
      name: 'Odometer Miles / KM',
      starting_value: '0',
      data_type: 'number',
      type: 'odometerMiles',
      prompts: {
        on_hire: {
          enabled: true,
          type: 'inline'
        },
        off_hire: {
          enabled: true,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'inline'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: true
    }
  ],
  [
    'serviceIntervalMiles',
    {
      name: 'Service Interval (Miles / KM)',
      starting_value: '0',
      data_type: 'number',
      type: 'serviceIntervalMiles',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'inline'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'serviceIntervalHours',
    {
      name: 'Service Interval (Engine Hours)',
      starting_value: '0',
      data_type: 'number',
      type: 'serviceIntervalHours',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'inline'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'serviceIntervalMonths',
    {
      name: 'Service Interval (Months)',
      starting_value: '6',
      data_type: 'number',
      type: 'serviceIntervalMonths',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'inline'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'lastServiceDate',
    {
      name: 'Last Service Date',
      starting_value: new Date().toISOString(),
      data_type: 'date',
      type: 'lastServiceDate',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'popup'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'lastServiceHours',
    {
      name: 'Last Service (Engine Hours)',
      starting_value: '0',
      data_type: 'number',
      type: 'lastServiceHours',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'popup'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'lastServiceMiles',
    {
      name: 'Last Service (Odomoter Miles / KM)',
      starting_value: '0',
      data_type: 'number',
      type: 'lastServiceMiles',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'popup'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'lastInspectionDate',
    {
      name: 'Last Inspection Date',
      starting_value: new Date().toISOString(),
      data_type: 'date',
      type: 'lastInspectionDate',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: true,
          type: 'popup'
        }
      },
      display_on_summary: true,
      display_on_public_page: true,
      append_to_hire_item_notes: false
    }
  ],
  [
    'inspectionIntervalWeeks',
    {
      name: 'Inspection interval (Weeks)',
      starting_value: '6',
      data_type: 'number',
      type: 'inspectionIntervalWeeks',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'popup'
        }
      },
      display_on_summary: false,
      display_on_public_page: false,
      append_to_hire_item_notes: false
    }
  ],
  [
    'motExpiryDate',
    {
      name: 'MOT Expiry Date',
      starting_value: new Date().toISOString(),
      data_type: 'date',
      type: 'motExpiryDate',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'popup'
        }
      },
      display_on_summary: false,
      display_on_public_page: false,
      append_to_hire_item_notes: false
    }
  ],
  [
    'taxExpiryDate',
    {
      name: 'Tax Expiry Date',
      starting_value: new Date().toISOString(),
      data_type: 'date',
      type: 'taxExpiryDate',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'popup'
        }
      },
      display_on_summary: false,
      display_on_public_page: false,
      append_to_hire_item_notes: false
    }
  ],
  [
    'insuranceExpiryDate',
    {
      name: 'Insurance Expiry Date',
      starting_value: new Date().toISOString(),
      data_type: 'date',
      type: 'insuranceExpiryDate',
      prompts: {
        on_hire: {
          enabled: false,
          type: 'inline'
        },
        off_hire: {
          enabled: false,
          type: 'inline'
        },
        workshop_job_complete: {
          enabled: false,
          type: 'popup'
        }
      },
      display_on_summary: false,
      display_on_public_page: false,
      append_to_hire_item_notes: false
    }
  ]
]);
